/**
 * # Workspace Decision Gate
 *
 * The single file that owns all access decisions for workspace scope.
 * Everything flows in, a FeatureDecision comes out.
 *
 */

import { createAvailableDecision, createBlockedDecision, type FeatureDecision } from '@frontend/scope-policy';
import type { SelectedScope } from '@frontend/scope-policy';
import { NotificationType } from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import type {
  CapabilityId,
  TopBarSurfaceId,
  GlobalPanelSurfaceId,
  EntryPointSurfaceId,
  BannerSurfaceId,
} from './types';
import {
  ALLOWED_FEATURES,
  ALLOWED_SETTINGS,
  ALLOWED_NOTIFICATIONS,
  ALLOWED_IN_NOTIFICATION_CENTER,
  TOP_BAR_PARENTS,
  PANEL_PARENTS,
  ENTRY_POINT_PARENTS,
  BANNER_PARENTS,
} from './registry';
import { resolvePath } from './route-resolution';

// ─────────────────────────────────────────────────────────────────────────────
// Decision constants
// ─────────────────────────────────────────────────────────────────────────────

/** Reusable available decision (no restrictions). */
export const AVAILABLE_DECISION: FeatureDecision = createAvailableDecision();

/** Standard blocked decision for workspace scope restrictions. */
export const BLOCKED_DECISION: FeatureDecision = createBlockedDecision({
  explanation: {
    code: 'unsupported-in-scope',
    messageKey: 'scope.notSupportedInCurrentScope',
    recommendedScopeType: 'location',
  },
});

/**
 * Decision for when the scope cannot be resolved (e.g. scope data still loading).
 * Fail-closed: features stay hidden until we confirm the user's actual scope.
 */
export const UNRESOLVED_SCOPE_DECISION: FeatureDecision = createBlockedDecision({
  explanation: {
    code: 'unresolved-scope',
    messageKey: 'scope.unresolved',
    recommendedScopeType: 'location',
  },
});

// ─────────────────────────────────────────────────────────────────────────────
// Capability and settings resolvers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Given a scope and capability, returns the workspace access decision.
 *
 * - No scope selected → AVAILABLE (fail-open during initialization)
 * - Workspace scope → check ALLOWED_FEATURES
 * - All other scopes → AVAILABLE (unrestricted)
 */
export const resolveCapabilityDecision = (
  scope: SelectedScope | null | undefined,
  capability: CapabilityId
): FeatureDecision => {
  if (!scope) return AVAILABLE_DECISION;

  switch (scope.scopeType) {
    case 'workspace':
      return ALLOWED_FEATURES.has(capability) ? AVAILABLE_DECISION : BLOCKED_DECISION;
    default:
      return AVAILABLE_DECISION;
  }
};

/**
 * Resolver for workspace settings sections that are not tied to a feature.
 */
export const resolveSettingsDecision = (scope: SelectedScope | null | undefined, section: string): FeatureDecision => {
  if (!scope) return AVAILABLE_DECISION;

  switch (scope.scopeType) {
    case 'workspace':
      return ALLOWED_SETTINGS.has(section) ? AVAILABLE_DECISION : BLOCKED_DECISION;
    default:
      return AVAILABLE_DECISION;
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Surface resolvers — path, top-bar, panel, entry-point
// ─────────────────────────────────────────────────────────────────────────────
//
// Each function resolves a surface to its parent capability via the workspace
// parent maps, then checks that capability through resolveCapabilityDecision.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Resolves a URL pathname to a FeatureDecision.
 *
 * 1. Path resolves to a feature → resolveCapabilityDecision(scope, feature)
 * 2. Path resolves to a settings section → resolveSettingsDecision(scope, section)
 * 3. Path is unresolved → AVAILABLE (not our concern)
 */
export const resolveWorkspacePathDecision = (scope: SelectedScope, path: string): FeatureDecision => {
  const resolution = resolvePath(path);

  switch (resolution.type) {
    case 'feature':
      return resolveCapabilityDecision(scope, resolution.feature);
    case 'settings':
      return resolveSettingsDecision(scope, resolution.section);
    case 'unresolved':
      return AVAILABLE_DECISION;
  }
};

/**
 * Resolves a top-bar surface (button or panel) to an access decision.
 * Uses TOP_BAR_PARENTS to find the parent capability.
 */
export const resolveWorkspaceTopBarDecision = (scope: SelectedScope, surfaceId: TopBarSurfaceId): FeatureDecision =>
  resolveCapabilityDecision(scope, TOP_BAR_PARENTS[surfaceId]);

/**
 * Resolves a global panel surface to an access decision.
 * Uses PANEL_PARENTS to find the parent capability.
 */
export const resolveWorkspacePanelDecision = (scope: SelectedScope, surfaceId: GlobalPanelSurfaceId): FeatureDecision =>
  resolveCapabilityDecision(scope, PANEL_PARENTS[surfaceId]);

/**
 * Resolves an entry-point surface (dashboard tile, quick action, shortlink)
 * to an access decision. Uses ENTRY_POINT_PARENTS to find the parent capability.
 */
export const resolveWorkspaceEntryPointDecision = (
  scope: SelectedScope,
  surfaceId: EntryPointSurfaceId
): FeatureDecision => resolveCapabilityDecision(scope, ENTRY_POINT_PARENTS[surfaceId]);

/**
 * Resolves a global banner surface to an access decision.
 * Uses BANNER_PARENTS to find the parent capability.
 *
 * Banners without a surfaceId are denied by default in workspace scope
 * (handled at the component level — see GlobalBannerDynamic).
 */
export const resolveWorkspaceBannerDecision = (scope: SelectedScope, surfaceId: BannerSurfaceId): FeatureDecision =>
  resolveCapabilityDecision(scope, BANNER_PARENTS[surfaceId]);

/**
 * Determines whether a banner should be shown in the current scope.
 *
 * - No scope → allowed (fail-open during initialization)
 * - Non-workspace scopes → always allowed (unrestricted)
 * - Workspace scope with surfaceId → check parent capability
 * - Workspace scope without surfaceId → denied (fail-closed: untagged banners hidden)
 */
export const isBannerAllowedInScope = (
  scope: SelectedScope | null | undefined,
  surfaceId?: BannerSurfaceId
): boolean => {
  if (!scope) return true;
  if (scope.scopeType !== 'workspace') return true;
  if (!surfaceId) return false; // Deny-by-default: untagged banners hidden in workspace
  return resolveWorkspaceBannerDecision(scope, surfaceId).availability === 'available';
};

/**
 * Determines whether a popup notification should show in the current scope.
 *
 * - Non-workspace scopes → always allowed (unrestricted)
 * - Workspace scope → must be in ALLOWED_NOTIFICATIONS
 * - No scope → allowed (fail-open during initialization)
 * - Unknown notification type → denied (fail-closed: not listed = not allowed)
 *
 * This is popup-only. Use `isAllowedInNotificationCenter` for the Notification Center.
 */
export const isPopupNotificationAllowed = (
  scope: SelectedScope | null | undefined,
  notificationType: string
): boolean => {
  if (!scope) return true;
  if (scope.scopeType !== 'workspace') return true;
  return ALLOWED_NOTIFICATIONS.has(notificationType);
};

/**
 * Determines whether a Notification Center item should be visible in the current scope.
 *
 * Same pattern as `isPopupNotificationAllowed`, but checks the Firestore
 * `NotificationType` values in ALLOWED_IN_NOTIFICATION_CENTER instead of
 * popup type strings.
 *
 * - Non-workspace scopes → always allowed (unrestricted)
 * - Workspace scope → must be in ALLOWED_IN_NOTIFICATION_CENTER
 * - No scope → allowed (fail-open during initialization)
 * - Unknown notification type → denied (fail-closed: not listed = not allowed)
 */
export const isAllowedInNotificationCenter = (
  scope: SelectedScope | null | undefined,
  notificationType: NotificationType
): boolean => {
  if (!scope) return true;
  if (scope.scopeType !== 'workspace') return true;
  return ALLOWED_IN_NOTIFICATION_CENTER.has(notificationType);
};
